// CompanyFieldsTab — the Data structure page's Company field tab: debounced
// search over the paged company-field list (POST /company-field/all) with
// Active/Hidden chips and dimmed hidden rows, plus CreateCompanyFieldModal
// serving create (opened from the page header's Add button — the open state
// lives with the page and arrives as a prop) and edit (opened from the kebab or
// the bar). The modal's CompanyField/LIST invalidation refreshes this list for
// free.
//
// Verb gates: Edit and Hide/Show ride `edit` (the status PATCH has no
// hide-shaped extra); Duplicate rides `create` because it creates a new field.
// `delete` has no affordance in this module (no endpoint either). All three act
// on exactly ONE field, so the selection bar shows them only for a single
// selection — the count and Clear stay for any size. The toolbar carries a
// three-category FilterDropdown (Status · Data source · Last updated via the
// shared date presets).
//
// NOTE: this tab has no sorting and no clickable rows. Its list payload carries
// no orderBy, so no column declares `sortable` and no `onSort` is passed —
// DataTable then draws no sort glyphs at all, rather than chevrons that look
// clickable and do nothing. There is no company-field detail view either, so
// `onRowClick` is omitted: the row kebab and the selection bar are the only
// action surfaces. (The other two tabs DO sort — their payloads have orderBy.)

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import {
  useDuplicateCompanyFieldMutation,
  useGetCompanyFieldsQuery,
  useSetCompanyFieldStatusMutation,
  type CompanyField,
  type CompanyFieldStatusFilter,
} from '@/api/endpoints/companyFieldsApi';
import { useGetDataSourceSelectionQuery } from '@/api/endpoints/datasourceApi';
import { FormDateFilter } from '@/api/endpoints/formsApi';
import {
  DATE_ANY,
  buildDateFilterOptions,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import useModuleAccess from '@/hooks/useModuleAccess';
import CreateCompanyFieldModal from './CreateCompanyFieldModal';
import { useCompanyFieldColumns } from './companyFieldColumns';

// Toolbar Filter keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['status', 'dataSource', 'lastUpdated'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));
const STATUS_VALUES: CompanyFieldStatusFilter[] = ['active', 'hidden'];

// Filter option dots — semantic status tokens per value (ProjectsPage idiom).
const STATUS_DOT: Record<CompanyFieldStatusFilter, string> = {
  active: 'var(--brand)',
  hidden: 'var(--text-3)',
};

interface CompanyFieldsTabProps {
  /** Create-modal open state, owned by the page (its Add button is in the header). */
  createOpen: boolean;
  onCloseCreate: () => void;
  /** Reports the UNFILTERED total up for the page's tab badge. */
  onCount?: (n: number) => void;
}

const CompanyFieldsTab = ({ createOpen, onCloseCreate, onCount }: CompanyFieldsTabProps) => {
  const { t } = useTranslation();

  // Verb gates (hidden, not disabled, when missing).
  const { canCreate, canEdit } = useModuleAccess('data_structure');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  // Keyed by id but holding the whole row: the bar's actions need the selected
  // field's values and its isHidden, and a row picked on page 1 is no longer in
  // `items` once you page away (the InspectionPage idiom).
  const [selected, setSelected] = useState<Map<string, CompanyField>>(new Map());
  const [editField, setEditField] = useState<CompanyField | null>(null);
  const [duplicateCompanyField] = useDuplicateCompanyFieldMutation();
  const [setCompanyFieldStatus] = useSetCompanyFieldStatusMutation();

  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom-range FROM/TO for the Last updated category.
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});

  // Data-source options — fetched up-front so the pane is ready on first open.
  const { data: dataSources = [] } = useGetDataSourceSelectionQuery();

  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('data_structure_cf_col_status'),
        icon: 'circle-dot',
        options: STATUS_VALUES.map((s) => ({
          id: s,
          label: t(`data_structure_cf_status_${s}`),
          dotColor: STATUS_DOT[s],
        })),
      },
      {
        key: 'dataSource',
        label: t('data_structure_cf_col_source'),
        icon: 'database',
        searchable: true,
        searchPlaceholder: t('data_structure_cf_filter_source_ph'),
        options: dataSources.map((d) => ({ id: d.id, label: d.schemaName })),
      },
      {
        key: 'lastUpdated',
        label: t('data_structure_cf_col_updated'),
        icon: 'history',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: buildDateFilterOptions(t),
      },
    ],
    [t, dataSources],
  );

  const lastUpdated = dateFilterOf(filters, 'lastUpdated');

  const { data, currentData, isLoading, isFetching, isError } = useGetCompanyFieldsQuery(
    {
      page,
      limit,
      search: debouncedSearch || undefined,
      status: filters.status.size ? ([...filters.status] as CompanyFieldStatusFilter[]) : undefined,
      dataSource: filters.dataSource.size ? [...filters.dataSource] : undefined,
      lastUpdated,
      lastUpdatedStart: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'start'),
      lastUpdatedEnd: dateRangeOf(dateRanges, 'lastUpdated', lastUpdated, 'end'),
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search or filter always starts from the first page.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges]);

  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);

  // Report the total up for the tab badge, but only while search + filters are
  // empty so the badge stays the true entity total, not the current narrowing.
  const filtersActive =
    !!debouncedSearch.trim() || FILTER_KEYS.some((key) => filters[key].size > 0);
  useEffect(() => {
    if (data && !filtersActive) onCount?.(total);
  }, [data, total, filtersActive, onCount]);

  const items = data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Counted from the loaded page — the response carries no global active/hidden
  // split, so this describes what is on screen.
  const hiddenCount = items.filter((f) => f.isHidden).length;
  const activeCount = items.length - hiddenCount;

  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const clearSelection = () => setSelected(new Map());

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((f) => f.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });
  const allSelected = items.length > 0 && items.every((f) => selected.has(f.id));
  const someSelected = !allSelected && items.some((f) => selected.has(f.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (items.every((f) => prev.has(f.id))) items.forEach((f) => next.delete(f.id));
      else items.forEach((f) => next.set(f.id, f));
      return next;
    });

  // The bar's actions apply to exactly one row.
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : null;

  const runDuplicate = async (row: CompanyField) => {
    try {
      await duplicateCompanyField(row.id).unwrap();
      toast.success(t('data_structure_cf_duplicated', { name: row.fieldTitle }));
      clearSelection();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const runToggleStatus = async (row: CompanyField) => {
    try {
      await setCompanyFieldStatus({ id: row.id, isHidden: !row.isHidden }).unwrap();
      toast.success(
        t(row.isHidden ? 'data_structure_cf_shown' : 'data_structure_cf_hidden', {
          name: row.fieldTitle,
        }),
      );
      clearSelection();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Declared after the handlers it forwards to the row kebab.
  const columns = useCompanyFieldColumns({
    onEdit: canEdit ? setEditField : undefined,
    onDuplicate: canCreate ? runDuplicate : undefined,
    onToggleStatus: canEdit ? runToggleStatus : undefined,
  });

  return (
    <>
      <div className='data-structure-page__toolbar'>
        <div className='data-structure-page__toolbar-lead'>
          <div className='data-structure-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('data_structure_cf_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='data-structure-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('data_structure_cf_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>
        {items.length > 0 && (
          <span className='data-structure-page__meta'>
            {t('data_structure_cf_meta', { active: activeCount, hidden: hiddenCount })}
          </span>
        )}
      </div>

      <Card padded={false} className='data-structure-page__panel'>
        {selected.size > 0 && (
          <div className='data-structure-page__selection-bar'>
            <div className='data-structure-page__selection-lead'>
              <span className='data-structure-page__selected'>
                {t('data_structure_cf_selected', { count: selected.size })}
              </span>
              {/* Edit/Duplicate/Hide act on ONE field, so they only appear
                  for a single selection — each behind its verb, and the
                  divider only when at least one can follow it. */}
              {onlySelected && (canEdit || canCreate) && (
                <>
                  <span className='data-structure-page__selection-divider' />
                  {canEdit && (
                    <Button
                      variant='ghost'
                      iconStart='pencil'
                      onClick={() => setEditField(onlySelected)}
                    >
                      {t('data_structure_cf_action_edit')}
                    </Button>
                  )}
                  {canCreate && (
                    <Button
                      variant='ghost'
                      iconStart='copy'
                      onClick={() => runDuplicate(onlySelected)}
                    >
                      {t('data_structure_cf_action_duplicate')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant='ghost'
                      iconStart={onlySelected.isHidden ? 'eye' : 'eye-off'}
                      onClick={() => runToggleStatus(onlySelected)}
                    >
                      {t(
                        onlySelected.isHidden
                          ? 'data_structure_cf_action_show'
                          : 'data_structure_cf_action_hide',
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
            <Button variant='ghost' onClick={clearSelection}>
              {t('button_clear')}
            </Button>
          </div>
        )}
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(f) => f.id}
          // Selection only feeds the bar's Edit/Duplicate/Hide — without
          // both verbs it would select into a bar of count + Clear.
          selectable={canEdit || canCreate}
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('data_structure_cf_select_all')}
          isRowMuted={(f) => f.isHidden}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1200}
          empty={
            <>
              <Icon name='building-2' size={26} />
              {t('data_structure_cf_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('data_structure_items_per_page')}
          rangeText={t('data_structure_cf_range', { from, to, total })}
        />
      </Card>

      {/* One instance for both modes — `field` set means edit. */}
      <CreateCompanyFieldModal
        open={createOpen || Boolean(editField)}
        field={editField}
        onClose={() => {
          onCloseCreate();
          setEditField(null);
        }}
      />
    </>
  );
};

export default CompanyFieldsTab;
